import { Container, Stack, Text, Title } from '@mantine/core'
import { TimeGrid } from '@mantine/dates'
import {
  InterviewSlot,
  InterviewsAvailableForBookingReturns,
} from 'modules/admissions/types.graphql'

import { useQuery } from '@apollo/client'
import { FullPageError } from 'components/FullPageComponents'
import { FullContentLoader } from 'components/Loading'
import { INTERVIEWS_AVAILABLE_FOR_BOOKING_QUERY } from 'modules/admissions/queries'
import { format } from 'util/date-fns'

export interface InterviewsAvailableProps {
  dateSelected: string
  handleCallback: (value: string[]) => void
  currentlySelected: string[]
}

export const InterviewsAvailableForBooking: React.FC<
  InterviewsAvailableProps
> = ({ dateSelected, handleCallback, currentlySelected }) => {
  const { data, error, loading, refetch } =
    useQuery<InterviewsAvailableForBookingReturns>(
      INTERVIEWS_AVAILABLE_FOR_BOOKING_QUERY,
      {
        pollInterval: 1_000,
        fetchPolicy: 'network-only',
        variables: {
          dateSelected: dateSelected,
        },
      }
    )

  if (error) return <FullPageError />

  if (loading || !data)
    return (
      <Container style={{ minHeight: '600px' }}>
        <FullContentLoader />
      </Container>
    )

  if (data.interviewsAvailableForBooking.length === 0) {
    return (
      <Container>
        <Title order={3}>Ingen intervjuer tilgjengelig</Title>
        <Text>
          Det er ingen intervjuer tilgjengelig for booking. Prøv igjen senere
          eller sjekk andre dager.
        </Text>
      </Container>
    )
  }
  return (
    <>
      {data.interviewsAvailableForBooking.map(interviewDay => (
        <Stack key={`${interviewDay.date}`}>
          <Title c={'dimmed'} order={3}>
            Tilgjengelige tidspunkter
          </Title>
          <Title order={5} tt={'uppercase'}>
            {format(new Date(interviewDay.date), 'EEEE dd MMM')}
          </Title>
          <InterviewDayTimeGrid
            slots={interviewDay.interviewSlots}
            handleCallback={handleCallback}
            currentlySelected={currentlySelected}
          />
        </Stack>
      ))}
    </>
  )
}

interface InterviewDayTimeGridProps {
  slots: InterviewSlot[]
  handleCallback: (value: string[]) => void
  currentlySelected: string[]
}

const InterviewDayTimeGrid: React.FC<InterviewDayTimeGridProps> = ({
  slots,
  handleCallback,
  currentlySelected,
}) => {
  const slotsByTime = new Map(
    slots.map(slot => [format(new Date(slot.interviewStart), 'HH:mm'), slot])
  )
  const selectedTime =
    [...slotsByTime].find(([, slot]) =>
      slot.interviewIds.some(id => currentlySelected.includes(id))
    )?.[0] ?? null

  return (
    <TimeGrid
      data={[...slotsByTime.keys()]}
      value={selectedTime}
      onChange={time => {
        if (time) handleCallback(slotsByTime.get(time)?.interviewIds ?? [])
      }}
      allowDeselect={false}
      disableTime={time => !slotsByTime.get(time)?.interviewIds.length}
      radius="md"
      simpleGridProps={{ cols: { base: 1, xs: 2 } }}
    />
  )
}
